import {
  contractsPartialUpdateMutation,
  leadsContractsListQueryKey,
  leadsLogsListOptions,
  opportunitiesContractsListOptions,
  opportunitiesLogsListOptions,
  ReturnContractReadable,
  ReturnContractStatusEnum
} from '@crm/api'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, Button, Dropdown, Flex, MenuProps } from 'antd'
import { ArrowDown2 } from 'iconsax-react'

import { useParams } from 'react-router-dom'

import { useStyles } from './styles'

type Props = {
  contract: ReturnContractReadable
  bordered?: boolean
}

const StatusLabel = ({ name }: { name?: ReturnContractStatusEnum }) => {
  const { t } = useLocales()
  const { styles } = useStyles({ color: name })

  return (
    <Flex align="center" gap={8}>
      <div className={styles.statusDot} />

      {t(`contracts.${name}`)}
    </Flex>
  )
}

export const ContractSignedStatus = ({ contract, bordered }: Props) => {
  const { t } = useLocales()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { id: leadId, opportunityId } = useParams()

  const { notification } = App.useApp()

  const { mutate, isPending } = useMutation({
    ...contractsPartialUpdateMutation(),
    onError: (error) => showApiErrors(error),
    onSuccess: () => {
      invalidateQueries([
        leadsContractsListQueryKey({ path: { lead_id: String(leadId) } }),
        leadsLogsListOptions({ path: { lead_id: String(leadId) } })
      ])

      if (opportunityId) {
        invalidateQueries([
          opportunitiesContractsListOptions({
            path: { opportunity_id: String(opportunityId) }
          }),
          opportunitiesLogsListOptions({
            path: { opportunity_id: String(opportunityId) }
          })
        ])
      }

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const items: MenuProps['items'] = Object.values(ReturnContractStatusEnum).map(
    (name) => ({
      label: <StatusLabel name={name} />,
      key: name
    })
  )

  const onClickStatus = (status: ReturnContractStatusEnum) => {
    if (!contract.id) return

    mutate({
      path: { id: contract.id },
      body: { status }
    })
  }

  const isSigned = contract?.status === ReturnContractStatusEnum.SIGNED

  return (
    <Dropdown
      trigger={isSigned ? ['contextMenu'] : ['click']}
      menu={{
        items,
        selectedKeys: contract?.status ? [contract.status] : [],
        onClick: ({ key }) => onClickStatus(key as ReturnContractStatusEnum)
      }}
    >
      <Button
        loading={isPending}
        icon={<ArrowDown2 size={16} variant="Bold" />}
        iconPosition="end"
        type={bordered ? 'default' : 'text'}
      >
        <StatusLabel name={contract?.status} />
      </Button>
    </Dropdown>
  )
}
